import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, X, Send, Paperclip, Bell, Check, CheckCheck, ChevronDown, Volume2, VolumeX,
  ArrowLeft, Maximize2, Minimize2, Smile, Reply, Pencil, Trash2, Copy, Users, MoreVertical, FileText,
  ArrowDown, Search,
} from 'lucide-react';
import { api, apiUrl, getToken } from '../api';

/**
 * Tímové okno vpravo dole (.449) – od .495 ako WhatsApp.
 *
 * Oto (.449): „Malé, neotravné, schovávacie okno vpravo dole" – chat medzi
 * nami a upozornenia (#zmienka, zápis obchodníka k leadu) s odkazom.
 * Oto (.495): „sprav ho trošku sofistikovanejší, ako taký WhatsApp."
 *
 * Čo pribudlo: zoznam rozhovorov (Tím + súkromne s každým kolegom), online /
 * naposledy videný, fajky ✓ / ✓✓, odpoveď s citáciou, reakcie smajlíkom,
 * úprava a zmazanie vlastnej správy, náhľad obrázkov, vkladanie obrázka
 * zo schránky, pretiahnutie súboru, oddeľovače dní, väčšie okno.
 *
 * Kto nie je v tíme, okno nevidí vôbec. Zbalené je to len guľaté tlačidlo
 * s číslom; nové upozornenie vyskočí na pár sekúnd ako malá karta nad ním.
 */
const PAMAT = 'tim_okno_otvorene';
const PAMAT_VELKE = 'tim_okno_velke';
const pamataj = (k, v) => { try { localStorage.setItem(k, v); } catch { /* nevadí */ } };
const precitaj = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

/* ── Zvuk (.450) ─────────────────────────────────────────────────────────
 * Krátke „cink" z Web Audio – žiadny súbor na stiahnutie. Prehliadač pustí
 * zvuk až po prvom kliknutí na stránku; preto sa zvukový kontext „odomkne"
 * pri prvom ťuknutí kdekoľvek v CRM. */
let zvukCtx = null;
const kontext = () => {
  try {
    if (!zvukCtx) zvukCtx = new (window.AudioContext || window.webkitAudioContext)();
    return zvukCtx;
  } catch { return null; }
};
if (typeof window !== 'undefined') {
  const odomkni = () => { kontext()?.resume?.(); };
  window.addEventListener('pointerdown', odomkni, { once: true });
  window.addEventListener('keydown', odomkni, { once: true });
}
export function cink() {
  const ctx = kontext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') ctx.resume();
    const t0 = ctx.currentTime;
    [[880, 0], [1320, 0.12]].forEach(([f, d]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + d);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + d + 0.25);
      o.connect(g).connect(ctx.destination);
      o.start(t0 + d);
      o.stop(t0 + d + 0.3);
    });
  } catch { /* bez zvuku, nič sa nedeje */ }
}

/* ── pomôcky ──────────────────────────────────────────────────────────── */

/* Čas zo servera je UTC „YYYY-MM-DD HH:MM:SS". */
const kedy = (v) => (v ? new Date(`${String(v).replace(' ', 'T')}Z`) : null);
const hhmm = (d) => (d ? d.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' }) : '');
const denKluc = (d) => (d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '');
function denNazov(d) {
  if (!d) return '';
  const dnes = new Date();
  const vcera = new Date(); vcera.setDate(dnes.getDate() - 1);
  if (denKluc(d) === denKluc(dnes)) return 'Dnes';
  if (denKluc(d) === denKluc(vcera)) return 'Včera';
  const rozdiel = (dnes - d) / 86400000;
  if (rozdiel < 6) return d.toLocaleDateString('sk-SK', { weekday: 'long' }).replace(/^./, (c) => c.toUpperCase());
  return d.toLocaleDateString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric' });
}
/* V zozname rozhovorov: dnes čas, inak „Včera" / deň / dátum. */
const casVZozname = (v) => { const d = kedy(v); if (!d) return ''; const n = denNazov(d); return n === 'Dnes' ? hhmm(d) : n; };
const naposledy = (v) => {
  const d = kedy(v);
  if (!d) return '';
  const n = denNazov(d);
  return `naposledy ${n === 'Dnes' ? 'dnes' : n === 'Včera' ? 'včera' : n} o ${hhmm(d)}`;
};
const velkost = (b) => (!b ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} kB`);

const FARBY = ['#e11d48', '#7c3aed', '#0891b2', '#ea580c', '#16a34a', '#2563eb', '#c026d3', '#ca8a04'];
const farba = (id) => FARBY[Math.abs(Number(id) || 0) % FARBY.length];
const iniciály = (meno) => String(meno || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();

const suborUrl = (id) => apiUrl(`/api/tim/subor/${id}?token=${encodeURIComponent(getToken() || '')}`);

const EMOJI = ['😀', '😂', '😊', '😍', '😎', '🤔', '😅', '😢', '😡', '👍', '👎', '👏', '🙏', '💪', '🔥', '🎉',
  '❤️', '✅', '❌', '⚠️', '📦', '🚚', '💶', '📞', '📧', '🛠️', '⏰', '👀', '🤝', '☕', '🍺', '🙂'];

function Avatar({ id, meno, skupina = false, online = false, velkost: v = 40 }) {
  return (
    <span className="relative inline-flex shrink-0">
      <span className="inline-flex items-center justify-center rounded-full font-semibold text-white"
        style={{ width: v, height: v, fontSize: v * 0.36, background: skupina ? '#475569' : farba(id) }}>
        {skupina ? <Users size={v * 0.5} /> : iniciály(meno)}
      </span>
      {online && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />}
    </span>
  );
}

/* Pozadie rozhovoru ako vo WhatsAppe – jemné bodky, bez obrázka. */
const POZADIE = {
  backgroundColor: '#efeae2',
  backgroundImage: 'radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px)',
  backgroundSize: '14px 14px',
};

export default function TimOkno({ user }) {
  const navigate = useNavigate();
  const [stav, setStav] = useState(null);
  /* `?tim=1` (alebo `?tim=u:5`) v adrese otvorí okno rovno – tak vedie
   * upozornenie z telefónu. */
  const zAdresy = new URLSearchParams(window.location.search).get('tim');
  const [otvorene, setOtvorene] = useState(() => !!zAdresy || precitaj(PAMAT) === '1');
  const [velke, setVelke] = useState(() => precitaj(PAMAT_VELKE) === '1');
  const [zalozka, setZalozka] = useState('chat');
  const [konv, setKonv] = useState(() => (zAdresy && /^u:\d+$/.test(zAdresy) ? zAdresy : zAdresy ? 'tim' : null));
  const [hladaj, setHladaj] = useState('');
  const [vyskok, setVyskok] = useState(null);
  const videne = useRef(null);            // najvyššie id upozornenia, ktoré už vyskočilo
  const poslednaSprava = useRef(null);    // najvyššie id správy, o ktorej už vieme
  const [zvuk, setZvuk] = useState(() => precitaj('tim_zvuk') !== '0');
  const zvukRef = useRef(zvuk);
  useEffect(() => { zvukRef.current = zvuk; pamataj('tim_zvuk', zvuk ? '1' : '0'); }, [zvuk]);

  const nacitajStav = useCallback(async () => {
    try {
      const s = await api.get('/api/tim/stav');
      setStav(s);
      if (!s.clen) return;
      const najnovsie = (s.upozornenia || [])[0];
      let zazvon = false;
      if (videne.current === null) {
        videne.current = najnovsie?.id || 0;           // po načítaní stránky nič nevyskakuje
      } else if (najnovsie && najnovsie.id > videne.current && !najnovsie.precitane_at) {
        videne.current = najnovsie.id;
        setVyskok(najnovsie);
        zazvon = true;
      }
      /* Nová správa od kolegu (vlastná sa neráta – tá neprečítaná nie je). */
      if (poslednaSprava.current === null) {
        poslednaSprava.current = s.posledna_sprava || 0;
      } else if ((s.posledna_sprava || 0) > poslednaSprava.current) {
        poslednaSprava.current = s.posledna_sprava;
        if (s.neprecitane > 0) zazvon = true;
      }
      if (zazvon && zvukRef.current) cink();
    } catch { /* ticho – okno je pomôcka */ }
  }, []);

  useEffect(() => {
    nacitajStav();
    const t = setInterval(nacitajStav, otvorene ? 8000 : 15000);
    return () => clearInterval(t);
  }, [nacitajStav, otvorene]);

  useEffect(() => {
    if (!vyskok) return undefined;
    const t = setTimeout(() => setVyskok(null), 9000);
    return () => clearTimeout(t);
  }, [vyskok]);

  if (!stav?.clen) return null;

  const prepni = (v) => { setOtvorene(v); pamataj(PAMAT, v ? '1' : '0'); if (v) setVyskok(null); };
  const prepniVelke = () => setVelke((v) => { pamataj(PAMAT_VELKE, v ? '0' : '1'); return !v; });

  const otvorOdkaz = async (u) => {
    try { await api.post('/api/tim/precitane', { upozornenia: [u.id] }); } catch { /* nevadí */ }
    setVyskok(null);
    nacitajStav();
    if (u.odkaz) navigate(u.odkaz);
  };
  const oznacVsetky = async () => {
    try { setStav(await api.post('/api/tim/precitane', { upozornenia: 'vsetky' })); } catch { /* nevadí */ }
  };

  const spolu = (stav.neprecitane || 0) + (stav.nove_upozornenia || 0);
  const konverzacie = stav.konverzacie || [];
  const aktualna = konverzacie.find((k) => k.konv === konv) || null;
  /* Vo veľkom okne je zoznam vždy vedľa, v malom sa striedajú. */
  const ukazZoznam = velke || !aktualna || zalozka !== 'chat';
  const ukazRozhovor = zalozka === 'chat' && !!aktualna;

  return (
    /* Na telefóne sedí dole spodná lišta (.465) – okno chatu sa nad ňu
       posunie, inak by ju bublina prekrývala presne tam, kde sú ikony. */
    <div className="no-print fixed bottom-20 right-4 z-40 flex flex-col items-end gap-2 lg:bottom-4">
      {vyskok && !otvorene && (
        <div className="w-72 rounded-xl bg-white p-3 text-sm shadow-xl ring-1 ring-slate-200">
          <div className="flex items-start gap-2">
            <Bell size={16} className="mt-0.5 shrink-0 text-emerald-600" />
            <button className="min-w-0 flex-1 text-left text-slate-700 hover:text-slate-900" onClick={() => otvorOdkaz(vyskok)}>
              {vyskok.text}
              {vyskok.odkaz && <span className="mt-0.5 block text-xs font-semibold text-emerald-700">Otvoriť →</span>}
            </button>
            <button className="text-slate-400 hover:text-slate-600" onClick={() => setVyskok(null)}><X size={14} /></button>
          </div>
        </div>
      )}

      {otvorene && (
        <div className={`flex overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 ${
          velke ? 'h-[min(680px,calc(100vh-7rem))] w-[min(860px,calc(100vw-2rem))]'
            : 'h-[min(560px,calc(100vh-7rem))] w-[min(380px,calc(100vw-2rem))]'}`}>
          {ukazZoznam && (
            <div className={`flex min-w-0 flex-col ${velke && ukazRozhovor ? 'w-[300px] shrink-0 border-r border-slate-200' : 'flex-1'}`}>
              <div className="flex items-center gap-1 bg-[#008069] px-3 py-2.5 text-white">
                <span className="text-base font-semibold">Tím</span>
                <span className="ml-auto flex items-center">
                  <IkonaTlacidlo title={zvuk ? 'Zvuk zapnutý – kliknutím vypnete' : 'Zvuk vypnutý – kliknutím zapnete'}
                    onClick={() => { setZvuk((z) => !z); if (!zvuk) cink(); }}>
                    {zvuk ? <Volume2 size={18} /> : <VolumeX size={18} />}
                  </IkonaTlacidlo>
                  {!ukazRozhovor && (
                    <>
                      <IkonaTlacidlo title={velke ? 'Menšie okno' : 'Väčšie okno'} onClick={prepniVelke}>
                        {velke ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
                      </IkonaTlacidlo>
                      <IkonaTlacidlo title="Schovať" onClick={() => prepni(false)}><ChevronDown size={20} /></IkonaTlacidlo>
                    </>
                  )}
                </span>
              </div>
              <div className="flex gap-1 border-b border-slate-100 bg-white px-2 pt-1.5">
                {[
                  { k: 'chat', n: 'Chaty', c: stav.neprecitane },
                  { k: 'upozornenia', n: 'Upozornenia', c: stav.nove_upozornenia },
                ].map((z) => (
                  <button key={z.k} onClick={() => setZalozka(z.k)}
                    className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-semibold ${zalozka === z.k
                      ? 'border-[#008069] text-[#008069]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                    {z.n}{z.c ? <span className="ml-1.5 rounded-full bg-[#25d366] px-1.5 text-[10px] font-bold text-white">{z.c}</span> : null}
                  </button>
                ))}
              </div>

              {zalozka === 'chat' ? (
                <ZoznamRozhovorov konverzacie={konverzacie} user={user} aktivna={konv}
                  hladaj={hladaj} setHladaj={setHladaj} onVyber={setKonv} />
              ) : (
                <Upozornenia stav={stav} user={user} onOtvor={otvorOdkaz} onVsetky={oznacVsetky} />
              )}
            </div>
          )}

          {ukazRozhovor && (
            <Rozhovor key={aktualna.konv} k={aktualna} stav={stav} user={user} velke={velke}
              onSpat={() => setKonv(null)} onVelke={prepniVelke} onSchovat={() => prepni(false)}
              onZmena={nacitajStav} zvukRef={zvukRef} poslednaSprava={poslednaSprava} />
          )}
        </div>
      )}

      {!otvorene && (
        <button onClick={() => prepni(true)} title="Tím – chat a upozornenia"
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-lg transition hover:scale-105 hover:bg-[#1fb855]">
          <MessageCircle size={26} />
          {spolu > 0 && (
            <span className="absolute -right-1 -top-1 min-w-[22px] rounded-full bg-red-600 px-1 text-center text-[11px] font-bold leading-[22px] text-white ring-2 ring-white">
              {spolu > 99 ? '99+' : spolu}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

function IkonaTlacidlo({ children, title, onClick, className = '' }) {
  return (
    <button type="button" title={title} onClick={onClick}
      className={`rounded-full p-1.5 text-white/90 hover:bg-white/15 hover:text-white ${className}`}>
      {children}
    </button>
  );
}

/* ── zoznam rozhovorov ────────────────────────────────────────────────── */

function ZoznamRozhovorov({ konverzacie, user, aktivna, hladaj, setHladaj, onVyber }) {
  const q = hladaj.trim().toLowerCase();
  const zoznam = q ? konverzacie.filter((k) => k.nazov.toLowerCase().includes(q)) : konverzacie;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-3 py-2">
        <label className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5">
          <Search size={15} className="text-slate-400" />
          <input value={hladaj} onChange={(e) => setHladaj(e.target.value)} placeholder="Hľadať kolegu"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none" />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {zoznam.map((k) => {
          const p = k.posledna;
          const odMna = p && Number(p.od_id) === Number(user?.id);
          const kto = !p ? '' : odMna ? 'Ty: ' : k.skupina ? `${String(p.od || '').split(' ')[0]}: ` : '';
          return (
            <button key={k.konv} onClick={() => onVyber(k.konv)}
              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 ${aktivna === k.konv ? 'bg-slate-100' : ''}`}>
              <Avatar id={k.user_id} meno={k.nazov} skupina={k.skupina} online={!k.skupina && k.online} velkost={46} />
              <span className="min-w-0 flex-1 border-b border-slate-100 pb-2.5">
                <span className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate font-semibold text-slate-900">{k.nazov}</span>
                  <span className={`shrink-0 text-[11px] ${k.neprecitane ? 'font-semibold text-[#25d366]' : 'text-slate-400'}`}>
                    {casVZozname(p?.created_at)}
                  </span>
                </span>
                <span className="mt-0.5 flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] text-slate-500">
                    {p ? `${kto}${p.text}` : k.skupina ? 'Spoločný chat celého tímu' : 'Napíš prvú správu'}
                  </span>
                  {!!k.neprecitane && (
                    <span className="min-w-[20px] shrink-0 rounded-full bg-[#25d366] px-1.5 text-center text-[11px] font-bold leading-5 text-white">
                      {k.neprecitane}
                    </span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Upozornenia({ stav, user, onOtvor, onVsetky }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      {stav.nove_upozornenia > 0 && (
        <div className="flex justify-end border-b border-slate-100 px-3 py-1.5">
          <button className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" onClick={onVsetky}>
            <Check size={13} /> Označiť všetko ako prečítané
          </button>
        </div>
      )}
      {!(stav.upozornenia || []).length && (
        <p className="p-4 text-center text-xs text-slate-400">
          Keď ťa niekto označí (#{user?.name}) alebo obchodník zapíše niečo k leadu, uvidíš to tu.
        </p>
      )}
      {(stav.upozornenia || []).map((u) => (
        <button key={u.id} onClick={() => onOtvor(u)}
          className={`flex w-full gap-3 border-b border-slate-100 px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${
            u.precitane_at ? 'text-slate-500' : 'bg-emerald-50/50 font-medium text-slate-800'}`}>
          <Bell size={16} className={`mt-0.5 shrink-0 ${u.precitane_at ? 'text-slate-300' : 'text-emerald-600'}`} />
          <span className="min-w-0 flex-1">
            <span className="block">{u.text}</span>
            <span className="mt-0.5 block text-[11px] text-slate-400">
              {casVZozname(u.created_at)}{u.odkaz ? ' · otvoriť →' : ''}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

/* ── rozhovor ─────────────────────────────────────────────────────────── */

function Rozhovor({ k, stav, user, velke, onSpat, onVelke, onSchovat, onZmena, zvukRef, poslednaSprava }) {
  const [spravy, setSpravy] = useState(null);
  const [starsie, setStarsie] = useState(true);
  const [text, setText] = useState('');
  const [posielam, setPosielam] = useState(false);
  const [odpoved, setOdpoved] = useState(null);
  const [uprava, setUprava] = useState(null);
  const [emojiOkno, setEmojiOkno] = useState(false);
  const [menu, setMenu] = useState(false);
  const [dole, setDole] = useState(true);
  const [noveDole, setNoveDole] = useState(0);
  const [tahanie, setTahanie] = useState(false);
  const [zvyraznena, setZvyraznena] = useState(null);
  const [nahlad, setNahlad] = useState(null);
  const box = useRef(null);
  const pole = useRef(null);
  const subor = useRef(null);
  const naSpodku = useRef(true);
  const vyskaPred = useRef(null);
  const posledneId = useRef(0);

  const admin = user?.role === 'admin';
  const clenovia = (stav.clenovia || []).filter((c) => c.id !== user?.id);

  const nacitaj = useCallback(async () => {
    try {
      const r = await api.get(`/api/tim/spravy?konv=${encodeURIComponent(k.konv)}&limit=80`);
      const nove = r.spravy || [];
      setSpravy((stare) => {
        const najstarsiaNova = nove[0]?.id ?? Infinity;
        const drzat = (stare || []).filter((x) => x.id < najstarsiaNova);
        return [...drzat, ...nove];
      });
      if (nove.length < 80) setStarsie((s) => s && false);
      const posledna = nove.at(-1)?.id || 0;
      /* Zvuk pri novej správe od kolegu, keď je rozhovor otvorený. */
      const odKolegov = nove.filter((x) => x.id > (posledneId.current || 0) && x.od_id !== user?.id);
      if (posledneId.current && odKolegov.length) {
        if (zvukRef.current) cink();
        if (!naSpodku.current) setNoveDole((n) => n + odKolegov.length);
      }
      if (posledna > (posledneId.current || 0)) posledneId.current = posledna;
      if (posledna > (poslednaSprava.current || 0)) poslednaSprava.current = posledna;
      if (posledna && (k.neprecitane > 0 || document.visibilityState === 'visible')) {
        await api.post('/api/tim/precitane', { konv: k.konv, chat_po: posledna });
        if (k.neprecitane > 0) onZmena();
      }
    } catch { /* nevadí */ }
  }, [k.konv, k.neprecitane, user?.id, zvukRef, poslednaSprava, onZmena]);

  useEffect(() => {
    nacitaj();
    const t = setInterval(nacitaj, 4000);
    return () => clearInterval(t);
  }, [nacitaj]);

  const nacitajStarsie = async () => {
    if (!spravy?.length || !starsie) return;
    try {
      vyskaPred.current = box.current?.scrollHeight || null;
      const r = await api.get(`/api/tim/spravy?konv=${encodeURIComponent(k.konv)}&limit=60&pred=${spravy[0].id}`);
      const st = r.spravy || [];
      if (st.length < 60) setStarsie(false);
      setSpravy((s) => [...st, ...(s || [])]);
    } catch { /* nevadí */ }
  };

  /* Po načítaní starších správ ostane obraz tam, kde bol. */
  useLayoutEffect(() => {
    if (vyskaPred.current != null && box.current) {
      box.current.scrollTop += box.current.scrollHeight - vyskaPred.current;
      vyskaPred.current = null;
    } else if (naSpodku.current && box.current) {
      box.current.scrollTop = box.current.scrollHeight;
    }
  }, [spravy]);

  const naSkrol = () => {
    const el = box.current;
    if (!el) return;
    const spodok = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    naSpodku.current = spodok;
    setDole(spodok);
    if (spodok) setNoveDole(0);
    if (el.scrollTop < 40) nacitajStarsie();
  };
  const skrolDole = () => {
    if (box.current) box.current.scrollTo({ top: box.current.scrollHeight, behavior: 'smooth' });
    setNoveDole(0);
  };

  const posli = async (f = null) => {
    const t = text.trim();
    if (!t && !f) return;
    setPosielam(true);
    try {
      if (uprava && !f) {
        await api.put(`/api/tim/spravy/${uprava.id}`, { text: t });
        setUprava(null);
      } else if (f) {
        const fd = new FormData();
        fd.append('subor', f);
        fd.append('text', t);
        fd.append('konv', k.konv);
        if (odpoved) fd.append('odpoved_na', String(odpoved.id));
        await api.post('/api/tim/spravy', fd);
      } else {
        await api.post('/api/tim/spravy', { text: t, konv: k.konv, odpoved_na: odpoved?.id || null });
      }
      setText('');
      setOdpoved(null);
      naSpodku.current = true;
      await nacitaj();
      onZmena();
    } catch (e) { alert(e.message); } // eslint-disable-line no-alert
    finally {
      setPosielam(false);
      if (subor.current) subor.current.value = '';
      pole.current?.focus();
    }
  };

  const reaguj = async (s, emoji) => {
    try {
      const nova = await api.post(`/api/tim/spravy/${s.id}/reakcia`, { emoji });
      setSpravy((xs) => xs.map((x) => (x.id === s.id ? nova : x)));
    } catch (e) { alert(e.message); } // eslint-disable-line no-alert
  };
  const zmaz = async (s) => {
    if (!window.confirm('Zmazať správu pre všetkých?')) return; // eslint-disable-line no-alert
    try { await api.del(`/api/tim/spravy/${s.id}`); await nacitaj(); onZmena(); }
    catch (e) { alert(e.message); } // eslint-disable-line no-alert
  };
  const upravit = (s) => { setUprava(s); setOdpoved(null); setText(s.text); setTimeout(() => pole.current?.focus(), 0); };
  const odpovedat = (s) => { setOdpoved(s); setUprava(null); setTimeout(() => pole.current?.focus(), 0); };
  const skocNa = (id) => {
    const el = box.current?.querySelector(`[data-sprava="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setZvyraznena(id);
    setTimeout(() => setZvyraznena(null), 1600);
  };

  const vymazHistoriu = async (celu) => {
    setMenu(false);
    const otazka = celu
      ? 'Vymazať CELÝ chat – Tím aj všetky súkromné rozhovory všetkých? Nedá sa to vrátiť.'
      : `Vymazať celú históriu rozhovoru „${k.nazov}"? Zmizne aj u ${k.skupina ? 'všetkých' : 'toho druhého'}. Nedá sa to vrátiť.`;
    if (!window.confirm(otazka)) return; // eslint-disable-line no-alert
    if (celu && !window.confirm('Naozaj? Zmažú sa všetky správy aj súbory.')) return; // eslint-disable-line no-alert
    try {
      await api.del('/api/tim/historia', celu ? {} : { konv: k.konv });
      setSpravy([]);
      onZmena();
    } catch (e) { alert(e.message); } // eslint-disable-line no-alert
  };

  /* Obrázok zo schránky (Ctrl+V) sa pošle rovno – ako vo WhatsAppe. */
  const vlozZoSchranky = (e) => {
    const f = [...(e.clipboardData?.files || [])][0];
    if (f) { e.preventDefault(); posli(f); }
  };

  /* #zmienka – ponuka mien po napísaní „#" (len v Tíme). */
  const zmienka = k.skupina ? text.match(/#([^\s#]*)$/) : null;
  const navrhy = zmienka
    ? clenovia.filter((c) => c.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .includes(zmienka[1].toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')))
    : [];
  const doplnZmienku = (c) => { setText((t) => t.replace(/#([^\s#]*)$/, `#${c.name} `)); pole.current?.focus(); };

  /* Výška poľa rastie s textom (max. 5 riadkov). */
  useLayoutEffect(() => {
    const el = pole.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(120, el.scrollHeight)}px`;
  }, [text]);

  const podtitul = k.skupina
    ? (k.online?.length ? `online: ${k.online.join(', ')}` : `${k.clenov} členovia`)
    : k.online ? 'online' : naposledy(k.videny_at) || 'offline';

  /* Skupiny bublín: ten istý autor bez prestávky → bez opakovania mena a chvosta. */
  const polozky = useMemo(() => {
    const out = [];
    let den = '';
    let predosla = null;
    for (const s of spravy || []) {
      const d = kedy(s.created_at);
      const dk = denKluc(d);
      if (dk !== den) { out.push({ typ: 'den', kluc: `d-${dk}`, text: denNazov(d) }); den = dk; predosla = null; }
      const prvy = !predosla || predosla.od_id !== s.od_id
        || (kedy(s.created_at) - kedy(predosla.created_at)) > 5 * 60000;
      out.push({ typ: 'sprava', kluc: s.id, s, prvy });
      predosla = s;
    }
    return out;
  }, [spravy]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col"
      onDragOver={(e) => { e.preventDefault(); setTahanie(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setTahanie(false); }}
      onDrop={(e) => { e.preventDefault(); setTahanie(false); const f = e.dataTransfer.files?.[0]; if (f) posli(f); }}>
      {/* hlavička */}
      <div className="flex items-center gap-2 bg-[#008069] px-2 py-2 text-white">
        {!velke && <IkonaTlacidlo title="Späť" onClick={onSpat}><ArrowLeft size={19} /></IkonaTlacidlo>}
        <Avatar id={k.user_id} meno={k.nazov} skupina={k.skupina} velkost={36} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate font-semibold">{k.nazov}</div>
          <div className="truncate text-[11.5px] text-white/80">{podtitul}</div>
        </div>
        <IkonaTlacidlo title={velke ? 'Menšie okno' : 'Väčšie okno'} onClick={onVelke}>
          {velke ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
        </IkonaTlacidlo>
        {admin && (
          <span className="relative">
            <IkonaTlacidlo title="Viac" onClick={() => setMenu((m) => !m)}><MoreVertical size={18} /></IkonaTlacidlo>
            {menu && (
              <div className="absolute right-0 top-9 z-20 w-60 overflow-hidden rounded-xl bg-white py-1 text-sm text-slate-700 shadow-xl ring-1 ring-black/10">
                <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50" onClick={() => vymazHistoriu(false)}>
                  <Trash2 size={15} className="text-slate-400" /> Vymazať históriu rozhovoru
                </button>
                <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-600 hover:bg-red-50" onClick={() => vymazHistoriu(true)}>
                  <Trash2 size={15} /> Vymazať celý chat (všetko)
                </button>
              </div>
            )}
          </span>
        )}
        <IkonaTlacidlo title="Schovať" onClick={onSchovat}><ChevronDown size={20} /></IkonaTlacidlo>
      </div>

      {/* správy */}
      <div ref={box} onScroll={naSkrol} onClick={() => { setMenu(false); }}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-2" style={POZADIE}>
        {spravy === null && <p className="mt-6 text-center text-xs text-slate-500">Načítavam…</p>}
        {spravy?.length === 0 && (
          <div className="mx-auto mt-6 max-w-[260px] rounded-lg bg-[#fff5c4] px-3 py-2 text-center text-xs text-slate-600 shadow-sm">
            {k.skupina ? 'Spoločný chat tímu. Napíš kolegom – #Meno označí konkrétneho človeka.'
              : `Súkromný rozhovor s ${k.nazov}. Vidíte ho len vy dvaja.`}
          </div>
        )}
        {starsie && spravy?.length >= 80 && (
          <div className="py-1 text-center">
            <button className="rounded-full bg-white/80 px-3 py-1 text-[11px] text-slate-500 shadow-sm" onClick={nacitajStarsie}>
              Staršie správy
            </button>
          </div>
        )}
        {polozky.map((p) => (p.typ === 'den' ? (
          <div key={p.kluc} className="sticky top-0 z-10 my-2 flex justify-center">
            <span className="rounded-lg bg-white/95 px-3 py-1 text-[11.5px] font-medium text-slate-600 shadow-sm">{p.text}</span>
          </div>
        ) : (
          <Bublina key={p.kluc} s={p.s} prvy={p.prvy} moja={p.s.od_id === user?.id} skupina={k.skupina}
            reakcie={stav.reakcie || []} admin={admin} zvyraznena={zvyraznena === p.s.id}
            onOdpoved={odpovedat} onReakcia={reaguj} onUprav={upravit} onZmaz={zmaz} onSkoc={skocNa}
            onObrazok={setNahlad} />
        )))}
      </div>

      {!dole && (
        <button onClick={skrolDole}
          className="absolute bottom-20 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-black/5">
          <ArrowDown size={18} />
          {noveDole > 0 && (
            <span className="absolute -top-1.5 -right-1 min-w-[18px] rounded-full bg-[#25d366] px-1 text-center text-[10px] font-bold leading-[18px] text-white">{noveDole}</span>
          )}
        </button>
      )}

      {tahanie && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-[#008069]/80 text-lg font-semibold text-white">
          Pusť súbor sem – pošle sa do rozhovoru
        </div>
      )}

      {/* písanie */}
      <div className="relative bg-[#f0f2f5] px-2 py-2">
        {(odpoved || uprava) && (
          <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5">
            <div className="min-w-0 flex-1 border-l-4 pl-2" style={{ borderColor: uprava ? '#008069' : farba(odpoved.od_id) }}>
              <div className="text-xs font-semibold" style={{ color: uprava ? '#008069' : farba(odpoved.od_id) }}>
                {uprava ? 'Upravuješ správu' : odpoved.od_id === user?.id ? 'Ty' : odpoved.od}
              </div>
              <div className="truncate text-xs text-slate-500">
                {(uprava || odpoved).text || ((uprava || odpoved).subor_nazov ? `📎 ${(uprava || odpoved).subor_nazov}` : '')}
              </div>
            </div>
            <button className="rounded-full p-1 text-slate-400 hover:bg-slate-100" title="Zrušiť"
              onClick={() => { setOdpoved(null); if (uprava) { setUprava(null); setText(''); } }}><X size={16} /></button>
          </div>
        )}

        {!!navrhy.length && (
          <div className="absolute bottom-full left-2 right-2 mb-1 overflow-hidden rounded-xl bg-white py-1 shadow-xl ring-1 ring-black/10">
            {navrhy.map((c) => (
              <button key={c.id} onClick={() => doplnZmienku(c)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                <Avatar id={c.id} meno={c.name} velkost={26} /> {c.name}
              </button>
            ))}
          </div>
        )}

        {emojiOkno && (
          <div className="absolute bottom-full left-2 mb-1 grid w-[272px] grid-cols-8 gap-0.5 rounded-xl bg-white p-2 shadow-xl ring-1 ring-black/10">
            {EMOJI.map((e) => (
              <button key={e} className="rounded-md p-1 text-xl hover:bg-slate-100"
                onClick={() => { setText((t) => t + e); pole.current?.focus(); }}>{e}</button>
            ))}
          </div>
        )}

        <div className="flex items-end gap-1.5">
          <div className="flex min-w-0 flex-1 items-end rounded-3xl bg-white px-1.5 shadow-sm">
            <button className="shrink-0 rounded-full p-2 text-slate-500 hover:text-slate-700" title="Smajlíky"
              onClick={() => setEmojiOkno((v) => !v)}><Smile size={21} /></button>
            <textarea ref={pole} rows={1} value={text}
              className="max-h-[120px] min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[14.5px] leading-5 outline-none"
              placeholder={uprava ? 'Uprav správu' : k.skupina ? 'Správa (#Meno označí kolegu)' : 'Správa'}
              onChange={(e) => setText(e.target.value)}
              onFocus={() => setEmojiOkno(false)}
              onPaste={vlozZoSchranky}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && navrhy.length) { e.preventDefault(); doplnZmienku(navrhy[0]); return; }
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); posli(); }
                if (e.key === 'Escape') { setOdpoved(null); setUprava(null); setEmojiOkno(false); }
                if (e.key === 'ArrowUp' && !text) {
                  const moja = [...(spravy || [])].reverse().find((x) => x.od_id === user?.id && !x.zmazana && x.text);
                  if (moja) { e.preventDefault(); upravit(moja); }
                }
              }} />
            {!uprava && (
              <button className="shrink-0 rounded-full p-2 text-slate-500 hover:text-slate-700" title="Poslať súbor alebo fotku"
                disabled={posielam} onClick={() => subor.current?.click()}><Paperclip size={20} /></button>
            )}
            <input ref={subor} type="file" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) posli(f); }} />
          </div>
          <button className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#008069] text-white shadow-sm hover:bg-[#017561] disabled:opacity-50"
            title={uprava ? 'Uložiť úpravu' : 'Poslať'} disabled={posielam || !text.trim()} onClick={() => posli()}>
            {uprava ? <Check size={20} /> : <Send size={19} className="-ml-0.5" />}
          </button>
        </div>
      </div>

      {nahlad && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6" onClick={() => setNahlad(null)}>
          <img src={suborUrl(nahlad.id)} alt={nahlad.subor_nazov || ''} className="max-h-full max-w-full rounded-lg shadow-2xl" />
          <a href={suborUrl(nahlad.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            className="absolute right-6 top-6 rounded-full bg-white/15 px-3 py-1.5 text-sm text-white hover:bg-white/25">
            Otvoriť originál
          </a>
        </div>
      )}
    </div>
  );
}

/* ── bublina ──────────────────────────────────────────────────────────── */

function Bublina({ s, prvy, moja, skupina, reakcie, admin, zvyraznena, onOdpoved, onReakcia, onUprav, onZmaz, onSkoc, onObrazok }) {
  const [akcie, setAkcie] = useState(false);
  const [reakcieOkno, setReakcieOkno] = useState(false);
  const cas = hhmm(kedy(s.created_at));
  const mozeZmazat = !s.zmazana && (moja || (admin && skupina));

  const kopiruj = () => { navigator.clipboard?.writeText(s.text || ''); setAkcie(false); };

  return (
    <div data-sprava={s.id}
      className={`group relative flex ${moja ? 'justify-end' : 'justify-start'} ${prvy ? 'mt-2' : 'mt-0.5'} ${s.reakcie?.length ? 'mb-3' : ''}`}
      onMouseLeave={() => { setAkcie(false); setReakcieOkno(false); }}>
      <div className={`relative max-w-[82%] rounded-lg px-2 pb-1 pt-1 text-[14px] leading-[19px] text-slate-900 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] transition ${
        moja ? 'bg-[#d9fdd3]' : 'bg-white'} ${prvy ? (moja ? 'rounded-tr-none' : 'rounded-tl-none') : ''} ${
        zvyraznena ? 'ring-2 ring-amber-400' : ''}`}>
        {/* chvost */}
        {prvy && (
          <span className={`absolute top-0 h-0 w-0 border-t-[8px] ${moja
            ? '-right-2 border-l-[8px] border-t-[#d9fdd3] border-l-transparent'
            : '-left-2 border-r-[8px] border-t-white border-r-transparent'}`} />
        )}

        {skupina && !moja && prvy && (
          <div className="mb-0.5 text-[12.5px] font-semibold" style={{ color: farba(s.od_id) }}>{s.od}</div>
        )}

        {s.odpoved && !s.zmazana && (
          <button onClick={() => onSkoc(s.odpoved.id)}
            className={`mb-1 block w-full rounded-md border-l-4 px-2 py-1 text-left ${moja ? 'bg-[#cfe9c7]' : 'bg-slate-100'}`}
            style={{ borderColor: farba(s.odpoved.od_id) }}>
            <span className="block text-[12px] font-semibold" style={{ color: farba(s.odpoved.od_id) }}>{s.odpoved.od}</span>
            <span className="line-clamp-2 block text-[12.5px] text-slate-600">{s.odpoved.text}</span>
          </button>
        )}

        {s.zmazana ? (
          <span className="italic text-slate-500">🚫 Správa bola zmazaná</span>
        ) : (
          <>
            {s.obrazok && (
              <button className="mb-1 block overflow-hidden rounded-md" onClick={() => onObrazok(s)}>
                <img src={suborUrl(s.id)} alt={s.subor_nazov || ''} loading="lazy"
                  className="max-h-60 w-full min-w-[160px] object-cover" />
              </button>
            )}
            {!!s.ma_subor && !s.obrazok && (
              <a href={suborUrl(s.id)} target="_blank" rel="noreferrer"
                className={`mb-1 flex items-center gap-2 rounded-md px-2 py-2 ${moja ? 'bg-[#cfe9c7]' : 'bg-slate-100'} hover:opacity-90`}>
                <FileText size={26} className="shrink-0 text-slate-500" />
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-medium">{s.subor_nazov}</span>
                  <span className="block text-[11px] text-slate-500">{velkost(s.subor_velkost)}</span>
                </span>
              </a>
            )}
            {s.text && <span className="whitespace-pre-wrap break-words"><TextSpravy text={s.text} /></span>}
          </>
        )}

        {/* čas a fajky – vpravo dole ako vo WhatsAppe */}
        <span className="float-right ml-2 mt-1.5 flex translate-y-0.5 items-center gap-0.5 whitespace-nowrap text-[10.5px] text-slate-500">
          {s.upravena && <span className="mr-0.5">upravené</span>}
          {cas}
          {moja && !s.zmazana && (s.precitana
            ? <CheckCheck size={15} className="text-[#53bdeb]" />
            : <Check size={14} className="text-slate-400" />)}
        </span>

        {/* reakcie */}
        {!!s.reakcie?.length && (
          <div className={`absolute -bottom-3.5 flex gap-0.5 ${moja ? 'right-2' : 'left-2'}`}>
            {s.reakcie.map((r) => (
              <button key={r.emoji} title={r.ludia.join(', ')} onClick={() => onReakcia(s, r.emoji)}
                className={`flex items-center gap-0.5 rounded-full bg-white px-1.5 py-px text-[13px] shadow ring-1 ${r.moja ? 'ring-[#25d366]' : 'ring-black/5'}`}>
                {r.emoji}{r.pocet > 1 && <span className="text-[10px] text-slate-500">{r.pocet}</span>}
              </button>
            ))}
          </div>
        )}

        {/* ponuka – ukáže sa pri prejdení myšou */}
        {!s.zmazana && (
          <button onClick={() => setAkcie((a) => !a)} title="Možnosti"
            className={`absolute right-1 top-1 rounded-full p-0.5 text-slate-400 opacity-0 transition group-hover:opacity-100 ${moja ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
            <ChevronDown size={16} />
          </button>
        )}
        {akcie && (
          <div className={`absolute top-6 z-20 w-44 overflow-hidden rounded-xl bg-white py-1 text-sm text-slate-700 shadow-xl ring-1 ring-black/10 ${moja ? 'right-0' : 'left-0'}`}>
            <MenuPolozka ikona={Reply} onClick={() => { onOdpoved(s); setAkcie(false); }}>Odpovedať</MenuPolozka>
            {s.text && <MenuPolozka ikona={Copy} onClick={kopiruj}>Kopírovať</MenuPolozka>}
            {moja && s.text && <MenuPolozka ikona={Pencil} onClick={() => { onUprav(s); setAkcie(false); }}>Upraviť</MenuPolozka>}
            {mozeZmazat && <MenuPolozka ikona={Trash2} cervena onClick={() => { setAkcie(false); onZmaz(s); }}>Zmazať</MenuPolozka>}
          </div>
        )}
      </div>

      {/* rýchla reakcia a odpoveď vedľa bubliny */}
      {!s.zmazana && (
        <div className={`flex items-center self-center opacity-0 transition group-hover:opacity-100 ${moja ? 'order-first mr-1' : 'ml-1'}`}>
          <span className="relative">
            <button className="rounded-full bg-white/70 p-1 text-slate-500 hover:bg-white" title="Reagovať"
              onClick={() => setReakcieOkno((v) => !v)}><Smile size={16} /></button>
            {reakcieOkno && (
              <span className={`absolute bottom-8 z-20 flex gap-0.5 rounded-full bg-white px-1.5 py-1 shadow-xl ring-1 ring-black/10 ${moja ? 'right-0' : 'left-0'}`}>
                {reakcie.map((e) => (
                  <button key={e} className="rounded-full p-0.5 text-xl transition hover:scale-125"
                    onClick={() => { onReakcia(s, e); setReakcieOkno(false); }}>{e}</button>
                ))}
              </span>
            )}
          </span>
          <button className="rounded-full bg-white/70 p-1 text-slate-500 hover:bg-white" title="Odpovedať"
            onClick={() => onOdpoved(s)}><Reply size={16} /></button>
        </div>
      )}
    </div>
  );
}

function MenuPolozka({ ikona: Ikona, children, onClick, cervena = false }) {
  return (
    <button onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 ${cervena ? 'text-red-600' : ''}`}>
      <Ikona size={15} className={cervena ? '' : 'text-slate-400'} /> {children}
    </button>
  );
}

/* Odkazy v texte sú klikateľné a #Meno je zvýraznené. */
function TextSpravy({ text }) {
  const casti = String(text).split(/(https?:\/\/[^\s]+|#[\p{L}]+(?:\s[\p{L}]+)?)/u);
  return casti.map((c, i) => {
    if (/^https?:\/\//.test(c)) {
      return <a key={i} href={c} target="_blank" rel="noreferrer" className="break-all text-[#027eb5] underline">{c}</a>;
    }
    if (/^#/.test(c)) return <span key={i} className="font-medium text-[#027eb5]">{c}</span>;
    return <span key={i}>{c}</span>;
  });
}
